import { Box, Skeleton, Text } from '@chakra-ui/react';
import { format } from 'd3-format';
import { isNumber } from 'lodash';
import { useMemo, useRef } from 'react';

import { useResizeObserver } from '@revelio/core';
import type { GetSentimentDataQuery } from '@revelio/data-access';
import {
  BarChart,
  FormatType,
  NEGATIVE_COLOR,
  POSITIVE_COLOR,
} from '@revelio/replots';

import { DirectionArrows } from '../../sentiment/sentimentV2/components/sentiment-effects-charts/direction-arrows';
import { normalizeBarScoresData } from '../../sentiment/sentimentV2/hooks/use-get-sentiment-reviews-data';
import type { SentimentBarDatum } from '../../sentiment/sentimentV2/types';
import { transformToSingleEntity } from '../../sentiment/sentimentV2/utils/transform-to-single-entity';

const formatNumber = format('.0%');

interface SentimentEffectsChartProps {
  data: GetSentimentDataQuery | undefined;
  isLoading: boolean;
  companyName: string | null;
  /** Chart container height in pixels (default 350) */
  height?: number;
}

/**
 * Standalone "Factors of Hiring / Factors of Attrition" bar chart.
 * Replicates the same chart shown on the Sentiment page for a single entity,
 * without requiring ChartMenuContext or other dashboard page dependencies.
 */
export const SentimentEffectsChart = ({
  data,
  isLoading,
  companyName,
  height = 350,
}: SentimentEffectsChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(containerRef);

  const barData = useMemo((): SentimentBarDatum[] => {
    if (!data) return [];
    const normalizedData = normalizeBarScoresData(data);
    return transformToSingleEntity(normalizedData).data;
  }, [data]);

  const barDataWithColors = useMemo(
    () =>
      barData.map((d) => ({
        ...d,
        color: d.value && d.value >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR,
      })),
    [barData]
  );

  const [min, max] = useMemo(() => {
    const values = barData.map((d) => ('value' in d ? (d.value ?? 0) : 0));
    if (values.length === 0) return [0, 0];
    return [Math.min(...values), Math.max(...values)];
  }, [barData]);

  if (isLoading) {
    return <Skeleton height="300px" borderRadius="md" />;
  }

  if (barData.length === 0) {
    return (
      <Box bg="gray.50" p={4} borderRadius="md" textAlign="center">
        <Text color="gray.500" fontSize="sm">
          No sentiment review data available for {companyName ?? 'this company'}
          .
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
        Sentiment Topics: {companyName ?? 'Current Employer'}
      </Text>
      <Box
        ref={containerRef}
        width="100%"
        height={`${height}px`}
        display="flex"
        flexDirection="column"
      >
        <Box flex="1 1 auto" minHeight={0}>
          <BarChart
            data={barDataWithColors}
            format={FormatType.PERCENTAGE}
            xAxis={{ min, max }}
            showBarLabels={false}
            tooltipContent={(tooltipData) => {
              const ttValue = tooltipData.rawValue ?? tooltipData.value;
              return (
                <div>
                  <Text fontWeight="semibold" color="#fff" fontSize="xs">
                    {`${tooltipData.label}: ${isNumber(ttValue) ? formatNumber(ttValue) : '-'}`}
                  </Text>
                  <Text color="#fff" fontSize="xs">
                    {`(n: ${tooltipData.count})`}
                  </Text>
                </div>
              );
            }}
          />
        </Box>
        <Box width="100%" height="25px">
          <DirectionArrows
            width={width}
            leftLabel="Factors of Attrition"
            rightLabel="Factors of Hiring"
          />
        </Box>
      </Box>
    </Box>
  );
};
